import React, { useState } from 'react';
import { useSyncedTab } from '../../hooks/useSyncedTab';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../Common/PermissionGuard';
import LoadingSpinner from '../Common/LoadingSpinner';
import CalendarView from './CalendarView';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useSyncedTab('overview');
  const { user, hasPermission } = useAuth();

  if (!user) {
    return <LoadingSpinner size="md" message="Loading user data..." />;
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            Overview
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'calendar'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            Calendar
          </button>
        </div>
      </div>

      {/* Conditional Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Welcome Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-8 sm:p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Welcome back, {String(user?.name || 'User')}!
              </h1>
              <p className="text-indigo-100">
                Here's what's happening with your business today.
              </p>
            </div>
            <div className="hidden sm:block">
              <div className="w-16 h-16 bg-indigo-500 bg-opacity-30 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Check if user has any quick action permissions */}
              {!hasPermission('CRM:LEADS:CREATE') && 
               !hasPermission('FINANCE:BILLS:CREATE') && 
               !hasPermission('INVENTORY:ITEMS:READ') && 
               !hasPermission('HRM:EMPLOYEES:READ') && 
               !hasPermission('CRM:STUDENTS:READ') && 
               !hasPermission('FINANCE:BUDGETS:READ') && 
               !hasPermission('REPORTS:SALES:READ') && 
               !hasPermission('REPORTS:INVENTORY:READ') && 
               !hasPermission('REPORTS:FINANCIAL:READ') && 
               !hasPermission('ADMIN:SETTINGS:READ') && (
                <div className="col-span-2 md:col-span-4 text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm">No quick actions available</p>
                  <p className="text-gray-400 text-xs mt-1">Contact your administrator for access</p>
                </div>
              )}
              
              <PermissionGuard permission="FINANCE:BILLS:CREATE">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">New Bill</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="FINANCE:BUDGETS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Budgets</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission={["REPORTS:SALES:READ", "REPORTS:INVENTORY:READ", "REPORTS:FINANCIAL:READ"]}>
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Reports</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="ADMIN:SETTINGS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Settings</span>
                </button>
              </PermissionGuard>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Account Info
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{String(user?.name || 'User')}</p>
                  <p className="text-sm text-gray-500">{String(user?.email || 'No email')}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Role</span>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      // Handle primaryRole
                      if (user?.primaryRole && typeof user.primaryRole === 'string') {
                        return user.primaryRole;
                      }
                      
                      // Handle role property
                      if (user?.role) {
                        if (typeof user.role === 'string') return user.role;
                        if (typeof user.role === 'object' && user.role.roleCode) return user.role.roleCode;
                        if (typeof user.role === 'object' && user.role.code) return user.role.code;
                        if (typeof user.role === 'object' && user.role.name) return user.role.name;
                      }
                      
                      // Handle roles array
                      if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                        const firstRole = user.roles[0];
                        if (typeof firstRole === 'string') return firstRole;
                        if (firstRole && typeof firstRole === 'object') {
                          if (firstRole.roleCode) return firstRole.roleCode;
                          if (firstRole.code) return firstRole.code;
                          if (firstRole.name) return firstRole.name;
                        }
                      }
                      
                      return 'USER';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    (user?.status && String(user.status).toLowerCase() === 'active') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user?.status ? String(user.status) : 'active'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Email Verified</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    Boolean(user?.emailVerified) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {Boolean(user?.emailVerified) ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Last Login</span>
                  <span className="text-sm text-gray-900">
                    {(() => {
                      if (!user?.lastLogin) return 'Never';
                      try {
                        const date = new Date(user.lastLogin);
                        return isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString();
                      } catch (error) {
                        return 'Never';
                      }
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <CalendarView />
      )}
    </div>
  );
};

export default Dashboard;